import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import debounce from "lodash.debounce";
import Icon from "@mdi/react";
import { mdiFilterVariant } from "@mdi/js";
import {
  FilterSheet,
  FilterClearButton,
} from "../../../ui/new_components/common/responsive";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import Pagination from "../../../ui/new_components/common/pagination";
import { urls } from "../../../utils/urls";
import { listRequestsApi } from "../service/request_desk_repository";
import { useRequestDeskAccess } from "../hooks/use_request_desk_access";
import {
  headingBar,
  isForbiddenRes,
  RD,
  RequestDeskRequest,
  statusStyle,
  STATUS_LABEL,
  typeStyle,
  TYPE_LABEL,
} from "../models/types";
import { CSSProperties } from "react";

const defaultFilter = () => ({
  type: "",
  status: "",
  entity_type: "",
  search: "",
  page: 1,
  count: 20,
});

const Chip: FunctionComponent<{ style?: CSSProperties; text: string }> = ({
  style,
  text,
}) => (
  <span
    className="px-2 py-0.5 rounded-full text-xs font-semibold"
    style={style}
  >
    {text}
  </span>
);

const RequestDeskListPage: FunctionComponent = () => {
  const { showToast } = useToast();
  const history = useHistory();
  const access = useRequestDeskAccess();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<RequestDeskRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [denied, setDenied] = useState(false);
  // Default to the first tab this user is allowed to see — a pure approver or
  // developer has no "My requests".
  const [tab, setTab] = useState(access.tabs[0]?.key ?? "");
  // Open inline on desktop, closed on mobile (the sheet is a drawer there).
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  const [filter, setFilter] = useState<any>(defaultFilter());

  /** Any filter change resets to page 1 — the old page may not exist anymore. */
  const setFilterValue = (patch: Record<string, any>) =>
    setFilter((f: any) => ({ ...f, ...patch, page: 1 }));

  const fetchData = async (f: any, t: string) => {
    if (!t) {
      setDenied(true);
      return;
    }
    setLoading(true);
    const params: any = { tab: t, page: f.page, count: f.count };
    if (f.type) params.type = f.type;
    if (f.status) params.status = f.status;
    if (f.entity_type) params.entity_type = f.entity_type;
    if (f.search) params.search = f.search;
    const res = await listRequestsApi(params);
    setLoading(false);
    if (res.success) {
      setDenied(false);
      setRows(res.data || []);
      setTotal(res.total || 0);
    } else if (isForbiddenRes(res)) {
      setDenied(true);
      setRows([]);
      setTotal(0);
    } else {
      showToast({ type: ToastType.error, text: res.error || "Failed to load" });
    }
  };

  // Filters apply themselves — no Apply button. Debounced so typing in the
  // search box doesn't fire a call per keystroke.
  const debouncedFetch = useMemo(
    () => debounce((f: any, t: string) => fetchData(f, t), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  // Below md the sheet is a drawer over the content — keep it closed there even
  // when the viewport shrinks after mount (rotate / resize).
  useEffect(() => {
    const handleResize = () => setShowFilter(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Permissions can change under an active session; fall back to a tab the user
  // is still allowed to see rather than looping on a 403.
  useEffect(() => {
    const keys = access.tabs.map((t) => t.key);
    if (keys.length > 0 && !keys.includes(tab)) setTab(keys[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.tabs.map((t) => t.key).join(",")]);

  useEffect(() => {
    debouncedFetch(filter, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, tab]);

  return (
    <div className="p-4">
      {loading && <LoadingWidget />}
      <div className="flex justify-between items-center mb-3">
        <h1
          className="text-lg font-semibold border-l-4 pl-2"
          style={headingBar}
        >
          Requests
        </h1>
        {/* Raise Request / Fault Dashboard live in the left menu (permission-gated). */}
        <div className="flex gap-2 items-center">
          <h6 className="font-normal text-xs">Filters</h6>
          <div
            onClick={() => setShowFilter((v) => !v)}
            className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
          >
            <Icon path={mdiFilterVariant} className="w-4 h-4" />
          </div>
        </div>
      </div>

      {denied ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-sm font-semibold mb-1">Access denied</div>
          <div className="text-xs text-gray-500">
            You do not have permission to view these requests. Ask an
            administrator for Request Desk access in Access Management.
          </div>
        </div>
      ) : (
        <>
          {/* tabs */}
          <div className="flex gap-1 border-b mb-3">
            {access.tabs.map((t) => (
              <div
                key={t.key}
                onClick={() => {
                  setTab(t.key);
                  setFilter((f: any) => ({ ...f, page: 1 }));
                }}
                className="px-4 py-2 text-sm font-semibold cursor-pointer border-b-2"
                style={
                  tab === t.key
                    ? { color: RD.accent, borderBottomColor: RD.accent }
                    : { color: RD.ink3, borderBottomColor: "transparent" }
                }
              >
                {t.label}
              </div>
            ))}
          </div>

          {/* filters — same shell/grid as the procurement lists, auto-applied */}
          <div className="mb-3">
            <FilterSheet
              show={showFilter}
              onClose={() => setShowFilter(false)}
              onReset={() => setFilter(defaultFilter())}
              gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
            >
              <div>
                <label>Search</label>
                <div className="flex items-center gap-1">
                  <input
                    className="border rounded px-2 py-1.5 text-sm w-full"
                    placeholder="Title / reference / request no…"
                    value={filter.search}
                    onChange={(e) => setFilterValue({ search: e.target.value })}
                  />
                  {filter.search && (
                    <FilterClearButton
                      title="Clear search"
                      onClick={() => setFilterValue({ search: "" })}
                    />
                  )}
                </div>
              </div>
              <div>
                <label>Request type</label>
                <div className="flex items-center gap-1">
                  <select
                    className="border rounded px-2 py-1.5 text-sm w-full"
                    value={filter.type}
                    onChange={(e) => setFilterValue({ type: e.target.value })}
                  >
                    <option value="">All types</option>
                    <option value="bug">Bug</option>
                    <option value="new_requirement">New Requirement</option>
                    <option value="service_request">Service Request</option>
                  </select>
                  {filter.type && (
                    <FilterClearButton
                      title="Clear request type"
                      onClick={() => setFilterValue({ type: "" })}
                    />
                  )}
                </div>
              </div>
              <div>
                <label>Status</label>
                <div className="flex items-center gap-1">
                  <select
                    className="border rounded px-2 py-1.5 text-sm w-full"
                    value={filter.status}
                    onChange={(e) => setFilterValue({ status: e.target.value })}
                  >
                    <option value="">All statuses</option>
                    {Object.keys(STATUS_LABEL).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                  {filter.status && (
                    <FilterClearButton
                      title="Clear status"
                      onClick={() => setFilterValue({ status: "" })}
                    />
                  )}
                </div>
              </div>
              <div>
                <label>Document type</label>
                <div className="flex items-center gap-1">
                  <select
                    className="border rounded px-2 py-1.5 text-sm w-full"
                    value={filter.entity_type}
                    onChange={(e) =>
                      setFilterValue({ entity_type: e.target.value })
                    }
                  >
                    <option value="">All entities</option>
                    <option value="PO">PO</option>
                    <option value="SO">SO</option>
                    <option value="ST">ST</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {filter.entity_type && (
                    <FilterClearButton
                      title="Clear document type"
                      onClick={() => setFilterValue({ entity_type: "" })}
                    />
                  )}
                </div>
              </div>
            </FilterSheet>
          </div>

          {/* table — scrolls internally so pagination stays at the page bottom */}
          <div
            className="border rounded overflow-auto bg-white"
            style={{ maxHeight: "90vh" }}
          >
            <table className="w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {[
                    "Request",
                    "Type",
                    "Title",
                    "Linked doc",
                    "Status",
                    "Raised by",
                    "Assigned",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left whitespace-nowrap text-gray-500 uppercase bg-gray-50"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        history.push(`${urls.request_desk_detail}/${r.id}`)
                      }
                    >
                      <td
                        className="px-3 py-2 font-semibold"
                        style={{ color: RD.accent }}
                      >
                        {r.request_no}
                      </td>
                      <td className="px-3 py-2">
                        <Chip
                          style={typeStyle(r.type)}
                          text={TYPE_LABEL[r.type] || r.type}
                        />
                      </td>
                      <td className="px-3 py-2">{r.title}</td>
                      <td className="px-3 py-2">
                        {r.service_request?.entity_ref || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Chip
                          style={statusStyle(r.status)}
                          text={STATUS_LABEL[r.status] || r.status}
                        />
                      </td>
                      <td className="px-3 py-2">{r.created_by_name || "—"}</td>
                      <td className="px-3 py-2">{r.assigned_to_name || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="h-40 flex items-center justify-center text-gray-400 text-lg">
                        No requests found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* pinned to the bottom of the viewport while the table scrolls */}
          <div className="sticky bottom-0 bg-white border-t px-2 py-1">
            <Pagination
              itemCount={rows.length}
              page={filter.page}
              total={total}
              count={filter.count}
              onChange={(page: number) =>
                setFilter((f: any) => ({ ...f, page }))
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default RequestDeskListPage;
